/**
 * Nocturne add-ons ("Complétez votre kit"): adds accessories to the cart the way the theme's own product
 * form does — a standard cart event carrying a promise, then the add request asking for the cart
 * sections — so the cart count, drawer contents and drawer opening all follow.
 * - Product page: checked [data-nx-kit-item] boxes are added right after the telescope itself.
 * - Anywhere else (cart drawer, home page, quiz): a [data-nx-add] button adds its variant and the drawer
 *   opens. Inside a product form it replaces the form's redirect to the cart page, which stays the
 *   fallback without JavaScript.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

let busy = false;

async function addLines(source, variantIds) {
  const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  busy = true;
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })), sections: sections.join(',') }),
    });
    const added = await res.json();
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'nocturne-kit', sourceId: 'nocturne-kit', itemCount: variantIds.length, sections: added.sections, didError: Boolean(added.status) },
    });
    return !added.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  } finally {
    busy = false;
  }
}

// Product page: when the telescope has been added, follow it with the checked accessories.
document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  if (busy || event.action !== 'add') return;
  const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (!form) return;
  const boxes = [...document.querySelectorAll('[data-nx-kit-item]:checked')];
  if (!boxes.length) return;
  event.promise?.then(({ detail }) => {
    if (detail?.didError) return;
    addLines(form, boxes.map((b) => b.value)).then((ok) => {
      if (ok) boxes.forEach((b) => (b.checked = false));
    });
  });
});

// One-tap add.
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-nx-add]') : null;
  if (!button) return;
  event.preventDefault();
  if (button.disabled) return;
  button.disabled = true;
  addLines(button, [button.dataset.variant]).finally(() => {
    if (button.isConnected) button.disabled = false;
  });
});
